import { useFlags } from "launchdarkly-react-client-sdk";
import { useEnvironmentType } from "../contexts/useEnvType";
import { useMyFeatureFlagsQuery } from "../types";

export type NutritionFlags = {
  isRecipeAndMenuItemRedesignEnabled: boolean;
  isTeamworksUserManagementEnabled: boolean;
  isAppSwitcherEnabled: boolean;
  showAxleUpsertTab: boolean;
  showDeleteAthleteButton: boolean;
  isUnityMessagingEnabled: boolean;
  isAmsDashboardEnabled: boolean;
  isEnergyExpenditureEnabled: boolean;
  isBulkDeleteMealPlansEnabled: boolean;
  isMenuBuilderMultiWeekCopyingEnabled: boolean;
  isAxleEventsEnabled: boolean;
  isUmaNotificationsEnabled: boolean;
  isBulkOrderingEnabled: boolean;
  isAiFoodLoggingEnabled: boolean;
  isTeamScopedPermissionsEnabled: boolean;
  isBulkDeleteMenuItemsEnabled: boolean;
  isBulkDeleteRecipesEnabled: boolean;
  menuAnalyticsDashboardEnabled: boolean;
  guestOrderingEnabled: boolean;
};

/** When isTactical and query is still loading, all flags default to false. */
const LOADING_LEGACY_FLAGS = {
  isRecipeAndMenuItemRedesignEnabled: false,
  twUserManagement: false,
  appSwitcher: false,
  amsDashboard: false,
  bulkDeleteMealPlans: false,
  axleEvents: false,
  umaNotifications: false,
  bulkOrdering: false,
  isAiFoodLoggingEnabled: false,
  teamScopedPermissions: true, // default to least permissive
  showAxleUpsertTab: false,
  isUnityMessagingEnabled: false,
  isEnergyExpenditureEnabled: false,
  isMenuBuilderMultiWeekCopyingEnabled: false,
  showDeleteAthleteButton: false,
  bulkDeleteMenuItems: false,
  bulkDeleteRecipes: false,
} as const;

function mapLegacyToNutritionFlags(legacy: {
  isRecipeAndMenuItemRedesignEnabled: boolean;
  twUserManagement: boolean;
  appSwitcher: boolean;
  axleEvents: boolean;
  umaNotifications: boolean;
  amsDashboard: boolean;
  bulkDeleteMealPlans: boolean;
  bulkOrdering: boolean;
  isAiFoodLoggingEnabled: boolean;
  teamScopedPermissions: boolean;
  showAxleUpsertTab: boolean;
  isUnityMessagingEnabled: boolean;
  isEnergyExpenditureEnabled: boolean;
  isMenuBuilderMultiWeekCopyingEnabled: boolean;
  showDeleteAthleteButton: boolean;
  bulkDeleteMenuItems: boolean;
  bulkDeleteRecipes: boolean;
}): NutritionFlags {
  return {
    // Recipe and menu item redesign is commercial-only.
    isRecipeAndMenuItemRedesignEnabled: false,
    isTeamworksUserManagementEnabled: legacy.twUserManagement,
    isAppSwitcherEnabled: legacy.appSwitcher,
    isAmsDashboardEnabled: legacy.amsDashboard,
    isBulkDeleteMealPlansEnabled: legacy.bulkDeleteMealPlans,
    isAxleEventsEnabled: legacy.axleEvents,
    isUmaNotificationsEnabled: legacy.umaNotifications,
    isBulkOrderingEnabled: legacy.bulkOrdering,
    isAiFoodLoggingEnabled: legacy.isAiFoodLoggingEnabled,
    isTeamScopedPermissionsEnabled: legacy.teamScopedPermissions,
    showAxleUpsertTab: legacy.showAxleUpsertTab,
    isUnityMessagingEnabled: legacy.isUnityMessagingEnabled,
    isEnergyExpenditureEnabled: legacy.isEnergyExpenditureEnabled,
    isMenuBuilderMultiWeekCopyingEnabled: legacy.isMenuBuilderMultiWeekCopyingEnabled,
    showDeleteAthleteButton: legacy.showDeleteAthleteButton,
    isBulkDeleteMenuItemsEnabled: legacy.bulkDeleteMenuItems,
    isBulkDeleteRecipesEnabled: legacy.bulkDeleteRecipes,
    // Menu Order Analytics is commercial-only
    menuAnalyticsDashboardEnabled: false,
    // Guest Ordering is commercial-only
    guestOrderingEnabled: false,
  };
}

/**
 * Returns LaunchDarkly flags in commercial; legacy org feature flags in tactical (GovCloud).
 * When isTactical, we do not call useFlags (no LD provider); hook order is still consistent
 * because isTactical is build-time (VITE_ENV_TYPE) and does not change during app lifecycle.
 */
export const useNutritionFlags = (): NutritionFlags => {
  const { isCommercial, isTactical } = useEnvironmentType();
  const { data } = useMyFeatureFlagsQuery({ skip: isCommercial });

  if (isTactical) {
    if (!data?.myFeatureFlags) return mapLegacyToNutritionFlags(LOADING_LEGACY_FLAGS);
    const legacy = { ...LOADING_LEGACY_FLAGS, ...data.myFeatureFlags };
    return mapLegacyToNutritionFlags(legacy);
  }

  return useFlags<NutritionFlags>();
};
